import React, { useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import useNavigationPage from '../../hooks/useNavigationPage';
import usePackingOrderService from '../../app/services/hooks/usePackingOrderService';
import { PackingOrderDataForm, PackingOrderFilters } from '../../app/models/PackingOrder';
import PackingOrderDataTable from './components/tables/PackingOrderDataTable';
import PackingOrderCreate from './components/PackingOrderCreate';

const breadcrumbs: BreadcrumbsItem[] = [
  { name: 'Órdenes de Empaque', url: '/packing-orders', isActive: true }
];

const PackingOrders = () => {
  const { navigationPage } = useNavigationPage();
  const { fetchingGetPackingOrders, getPackingOrders, deletePackingOrder } = usePackingOrderService();

  const [packingOrders, setPackingOrders] = useState<PackingOrderDataForm[]>([]);
  const [showingCreate, setShowingCreate] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);

  const [filter, setFilter] = useState<PackingOrderFilters>({
    page: 1,
    per_page: 10,
    sort: 'id',
    order: 'desc'
  });

  useEffect(() => {
    reloadTable();
  }, [filter.page, filter.per_page, filter.sort, filter.order]);

  const reloadTable = () => {
    getPackingOrders(filter, {
      onSuccess: (response: ServiceResponse) => {
        setPackingOrders(response.data.packing_orders || []);
        setTotalRows(response.data.total_rows || 0);
      },
      onError: (response: ServiceResponse) => {
        toast.error(response.message, { autoClose: 2000 });
        if (response.data?.to_dashboard) navigationPage('/');
      }
    });
  };

  const showEdit = (id: number) => {
    navigationPage('/packing-orders/' + id + '/edit');
  };
  const showCreate = () => setShowingCreate(true);
  const hideCreate = () => setShowingCreate(false);

  const destroy = (id: number) => {
    const _text = 'Está a punto de eliminar la orden de empaque #' + id;
    useReactConfirmAlert().requestConfirmation({
      title: '¿Estás seguro?',
      message: _text,
      buttons: {
        confirmButton: {
          label: 'Sí, confirmar',
          onClick: () => {
            deletePackingOrder(id, {
              onSuccess: (response: ServiceResponse) => {
                useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message });
                reloadTable();
              },
              onError: (response: ServiceResponse) => {
                useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
              }
            });
          }
        },
        cancelButton: {
          label: 'No, cancelar',
          onClick: () => {
            setTimeout(() => {
              useReactConfirmAlert().infoAlert({ title: 'Cancelado', message: 'La orden no se ha eliminado.' });
            }, 0);
          }
        }
      }
    });
  };

  return (
    <>
      <Breadcrumbs pageSection="Órdenes de Empaque" breadcrumbs={breadcrumbs} />

      <DefaultCard>
        <DefaultToolBar left={<ButtonCreate callbackCreate={showCreate} title="Nueva orden de empaque" />} />
        <PackingOrderDataTable
          packingOrders={packingOrders}
          totalRows={totalRows}
          loading={fetchingGetPackingOrders}
          edit={showEdit}
          destroy={destroy}
          filter={filter}
          setFilter={setFilter}
          callbackSuccess={reloadTable}
        />
      </DefaultCard>

      {showingCreate && (
        <DefaultModal
          show={showingCreate}
          handleClose={hideCreate}
          title="Crear orden de empaque"
          backdrop={true}
          showFooter={false}
          size="lg"
        >
          <PackingOrderCreate
            onSaved={(packingOrderId?: number) => {
              hideCreate();
              if (packingOrderId) {
                navigationPage('/packing-orders/' + packingOrderId + '/edit');
              } else {
                reloadTable();
              }
            }}
            onCancel={hideCreate}
            onError={hideCreate}
          />
        </DefaultModal>
      )}

    </>
  );
};

export default PackingOrders;
